import { useTranslation } from 'module/hooks/use-translation';
import {
  Control,
  OptionTypeBase,
  Select,
  SelectProps,
} from 'module/ui/components/atoms/select';
import { SingleLineEllipsis } from 'module/ui/components/styles';
import { Body01 } from 'module/ui/ozone/atoms/typography/body';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { List } from 'antd';
import { ListItemMetaProps } from 'antd/lib/list/Item';
import { initial } from 'lodash';
import { FC, useMemo } from 'react';
import { components, defaultTheme } from 'react-select';
import styled, { css } from 'styled-components';
import { VirtualizedMenuList } from './virtualised-list';
import { FontAwesomeIcon } from '../../atoms/icon/icon';

const selectStyles = {
  menu: () => ({
    boxShadow: 'none',
    flex: 1,
    overflow: 'hidden',
    marginTop: 'var(--spacing-05) !important',
  }),
  menuList: (provided) => ({
    ...provided,
    paddingTop: 0,
    paddingBottom: 0,
    maxHeight: initial,
    height: '100%',
  }),
  control: (provided) => ({ ...provided, margin: '0 var(--spacing-05)' }),
  group: (provided) => ({ ...provided, paddingTop: 0, paddingBottom: 0 }),
  groupHeading: (provided) => ({ ...provided, marginBottom: 0, flex: 1 }),
  option: (provided) => ({
    ...provided,
    paddingLeft: 'var(--spacing-05)',
    paddingRight: 'var(--spacing-05)',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    display: 'flex !important',
  }),
};

const StyledDropdownIndicator = styled.div.attrs({
  theme: {
    ...defaultTheme,
  },
})`
  height: var(--spacing-05);
  margin-left: var(--spacing-03);
  margin-right: var(--spacing-03);
`;

const DropdownIndicator = (props) => (
  <StyledDropdownIndicator {...props}>
    <FontAwesomeIcon icon={faSearch} />
  </StyledDropdownIndicator>
);

const ListItem = styled(List.Item)`
  padding: 0;
`;

type MetaProps = ListItemMetaProps & {
  isFocused: boolean;
  isSelected: boolean;
};

const ListItemMeta = styled(List.Item.Meta)<MetaProps>`
  align-items: center;
  & .ant-list-item-meta-title {
    margin-bottom: 0;
    ${SingleLineEllipsis};
    ${Body01};
    ${({ isSelected }) =>
      isSelected
        ? css`
            color: var(--white-100) !important;
          `
        : css`
            color: var(--gray-100) !important;
          `};
  }
  & .ant-list-item-meta-description {
    ${Body01};
    ${({ isSelected }) =>
      isSelected
        ? css`
            color: var(--white-100);
          `
        : css`
            color: var(--text-02);
          `};
    font-weight: normal;
  }
`;

const StyledOption = styled(components.Option).attrs({
  theme: {
    ...defaultTheme,
  },
})``;

const CustomOption = (props): React.JSX.Element => {
  const { data, isSelected, isFocused } = props;

  return (
    <StyledOption {...props}>
      <ListItem>
        <ListItemMeta
          isFocused={isFocused}
          isSelected={isSelected}
          title={data.label}
        />
      </ListItem>
    </StyledOption>
  );
};

export const GroupedDropdownSelect: FC<SelectProps<OptionTypeBase, false>> = (
  props,
): React.JSX.Element => {
  const { components: customComponents, options, ...rest } = props;
  const isDefault = useMemo(
    () =>
      (options &&
        options?.length > 0 &&
        (options as any)?.[0]?.id === 'default') ??
      true,
    [options],
  );

  return (
    <Select
      autoFocus
      menuIsOpen
      isClearable
      backspaceRemovesValue
      components={{
        ClearIndicator: null,
        IndicatorSeparator: null,
        DropdownIndicator,
        Control,
        Option: CustomOption,
        MenuList: VirtualizedMenuList,
        ...customComponents,
      }}
      controlShouldRenderValue={false}
      hideSelectedOptions={false}
      placeholder={useTranslation('search')}
      tabSelectsValue={false}
      styles={selectStyles}
      {...rest}
      options={isDefault ? (options as any)[0]?.options : options}
      grouped={!isDefault}
      type="basic"
    />
  );
};
